<!--
  OrderLtvV2View.vue — LTV 报告V2页面（按维度）
  基于首购日期的 Cohort LTV 分析，按自定义维度查询，展示 M0~Mn 各阶段订单、收入及留存率，
  以及投放消耗、ROI、利润等运营指标。
  功能：日期范围筛选、维度筛选、分页查询、上传人工录入数据、重置上传字段、CSV 下载。
  接口：
    GET  /balance/order/stats/cohort/dimension/list
    GET  /balance/order/stats/cohort/v2/daily/report
    POST /balance/order/stats/cohort/v2/daily/report/upload
    POST /balance/order/stats/cohort/v2/daily/report/reset
-->
<template>
  <div>
    <!-- 搜索栏 -->
    <a-card :bordered="false" style="margin-bottom: 16px">
      <a-form layout="inline" :model="searchForm">
        <a-form-item label="开始日期">
          <a-date-picker v-model:value="startDateVal" value-format="YYYY-MM-DD" placeholder="请选择开始日期"
            :allow-clear="true" />
        </a-form-item>
        <a-form-item label="结束日期">
          <a-date-picker v-model:value="endDateVal" value-format="YYYY-MM-DD" placeholder="请选择结束日期"
            :allow-clear="true" />
        </a-form-item>
        <a-form-item label="统计维度">
          <a-select v-model:value="searchForm.dimensionId" style="width: 320px" placeholder="请选择维度"
            :options="dimensionOptions" @change="handleSearch" />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" :loading="loading" @click="handleSearch">查询</a-button>
            <a-button @click="handleReset">重置搜索条件</a-button>
            <a-button v-if="hasButton('ltv-v2:download')" :loading="downloading" @click="handleDownload">下载</a-button>
            <a-button v-if="hasButton('ltv-v2:upload')" @click="showUploadModal = true">上传数据</a-button>
            <a-button v-if="hasButton('ltv-v2:reset')" danger :loading="resetting"
              @click="handleResetUpload">重置上传字段</a-button>
            <a-button v-if="hasButton('ltv-v2:dimension')" @click="openDimModal">维度管理</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 数据表格 -->
    <a-card :bordered="false">
      <template #title>
        <span class="ltv-collapse-toggle" @click="reportCollapsed = !reportCollapsed">
          <CaretDownOutlined v-if="!reportCollapsed" style="margin-right: 6px; font-size: 12px" />
          <CaretRightOutlined v-else style="margin-right: 6px; font-size: 12px" />
          LTV报告V2（维度）
        </span>
      </template>
      <a-table v-show="!reportCollapsed" :columns="columns" :data-source="tableData" :loading="loading"
        :pagination="pagination" row-key="rowKey" :scroll="{ x: 'max-content', y: 'calc(100vh - 280px)' }" size="small"
        bordered class="ltv-table" @change="handleTableChange">
        <template #summary v-if="tableData.length">
          <a-table-summary fixed>
            <a-table-summary-row class="summary-row">
              <a-table-summary-cell v-for="(cell, idx) in summaryCells" :key="idx" :index="idx"
                :align="idx < 2 ? 'left' : 'center'">
                <span style="white-space: nowrap; font-weight: 600">
                  {{ cell }}
                </span>
              </a-table-summary-cell>
            </a-table-summary-row>
          </a-table-summary>
        </template>
      </a-table>
    </a-card>

    <!-- 上传弹窗 -->
    <a-modal v-model:open="showUploadModal" title="上传人工录入数据" :footer="null" :destroy-on-close="true">
      <a-form layout="vertical">
        <a-form-item label="统计维度" :required="true">
          <a-select v-model:value="uploadDimensionId" style="width: 100%" placeholder="请选择维度"
            :options="dimensionOptions" />
        </a-form-item>
        <a-form-item label="xlsx 文件">
          <a-upload :before-upload="() => false" :max-count="1" accept=".xlsx" @change="handleFileChange">
            <a-button>选择文件</a-button>
          </a-upload>
        </a-form-item>
        <a-form-item>
          <a-button type="primary" :loading="uploading" @click="handleUpload">提交上传</a-button>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 维度管理弹窗 -->
    <a-modal v-model:open="showDimModal" title="维度管理" :footer="null" :width="700" :destroy-on-close="true">
      <a-table :columns="dimColumns" :data-source="dimList" :loading="dimLoading" :pagination="false" row-key="id"
        size="small" bordered>
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'name'">
            <a-input v-if="dimEditingId === record.id" v-model:value="dimEditForm.name" size="small"
              style="width: 100%" />
            <span v-else>{{ record.name }}</span>
          </template>
          <template v-if="column.key === 'sort'">
            <a-input-number v-if="dimEditingId === record.id" v-model:value="dimEditForm.sort" size="small" :min="0"
              style="width: 80px" />
            <span v-else>{{ record.sort }}</span>
          </template>
          <template v-if="column.key === 'isEnabled'">
            <a-switch v-if="dimEditingId === record.id" v-model:checked="dimEditForm.isEnabled" size="small" />
            <a-tag v-else :color="record.isEnabled ? 'green' : 'red'">
              {{ record.isEnabled ? '启用' : '禁用' }}
            </a-tag>
          </template>
          <template v-if="column.key === 'action'">
            <a-space v-if="dimEditingId === record.id">
              <a-button type="link" size="small" :loading="dimSaving" @click="handleDimSave(record)">保存</a-button>
              <a-button type="link" size="small" @click="dimEditingId = null">取消</a-button>
            </a-space>
            <a-button v-else type="link" size="small" @click="startDimEdit(record)">修改</a-button>
          </template>
        </template>
      </a-table>
    </a-modal>

    <!-- 预估回本模块 -->
    <a-card v-if="isAdmin()" :bordered="false" style="margin-top: 16px">
      <template #title>
        <span class="ltv-collapse-toggle" @click="estimateCollapsed = !estimateCollapsed">
          <CaretDownOutlined v-if="!estimateCollapsed" style="margin-right: 6px; font-size: 12px" />
          <CaretRightOutlined v-else style="margin-right: 6px; font-size: 12px" />
          预估回本分析
        </span>
      </template>
      <div v-show="!estimateCollapsed">
        <!-- 留存率表 -->
        <div style="margin-bottom: 8px">
          <a-button v-if="hasButton('ltv-v2:retention-save')" size="small" :loading="retentionSaving"
            @click="handleSaveRetentionRates">保存留存率</a-button>
        </div>
        <div style="margin-bottom: 16px; overflow-x: auto">
          <table class="estimate-table" border="1" cellspacing="0" cellpadding="4">
            <thead>
              <tr>
                <th>订单分成收入单价</th>
                <th v-for="n in 16" :key="n - 1">M{{ n - 1 }}留存率</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="text-align: center; white-space: nowrap">
                  <a-input-number v-model:value="estUnitPrice" :min="0" :step="0.1" size="small"
                    style="width: 90px" /> 元
                </td>
                <td v-for="n in 16" :key="n - 1" style="text-align: center; white-space: nowrap">
                  <a-input-number
                    :value="estRetentionRates[n - 1] != null ? +(estRetentionRates[n - 1] * 100).toFixed(2) : null"
                    @update:value="val => estRetentionRates[n - 1] = val != null ? val / 100 : null" :min="0" :max="100"
                    :step="0.1" size="small" style="width: 80px" />%
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 输入参数 -->
        <div style="margin-bottom: 8px; display: flex; flex-wrap: wrap; gap: 16px; align-items: center">
          <span>
            投放首次价格(元)：
            <a-input-number v-model:value="estFirstPrice" :min="0" :step="0.1" style="width: 120px" />
          </span>
          <span>
            原始获客成本(元)：
            <a-input-number v-model:value="estAcquisitionCost" :min="0" :step="0.1" style="width: 120px" />
          </span>
          <span>
            M1留存率：
            <a-input-number v-model:value="estM1Retention" :min="0" :max="100" :step="0.1" size="small"
              style="width: 100px" :formatter="v => v != null ? v + '%' : ''"
              :parser="v => v ? parseFloat(v.replace('%', '')) : null" />
            <a-button size="small" type="link" @click="resetM1Retention">自动计算</a-button>
          </span>
          <span>
            退款率：
            <a-input-number v-model:value="estRefundRate" :min="0" :max="100" :step="0.1" size="small"
              style="width: 100px" />%
            <a-button size="small" type="link" @click="resetRefundRate">自动计算</a-button>
          </span>
        </div>
        <div style="margin-bottom: 16px">
          <span>{{ estEffectiveUnitPriceDisplay }}获客成本：<b>{{ estAdjustedCostDisplay }}</b></span>
          <span style="margin-left: 16px">手续费率：<b>{{ (estFeeRate * 100).toFixed(0) }}%</b></span>
        </div>

        <!-- 实时计算表格 -->
        <div style="overflow-x: auto">
          <table class="estimate-table" border="1" cellspacing="0" cellpadding="4">
            <thead>
              <tr>
                <th></th>
                <th v-for="n in (estMaxM + 1)" :key="n - 1">M{{ n - 1 }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>收入</b></td>
                <td v-for="n in (estMaxM + 1)" :key="n - 1" style="text-align: right">{{ estCalcData.revenue[n -
                  1]?.toFixed(2) ?? '-' }}</td>
              </tr>
              <tr>
                <td><b>累计收入</b></td>
                <td v-for="n in (estMaxM + 1)" :key="n - 1" style="text-align: right">{{ estCalcData.cumulativeRevenue[n
                  -
                  1]?.toFixed(2) ?? '-' }}</td>
              </tr>
              <tr>
                <td><b>通道+提现服务费</b></td>
                <td v-for="n in (estMaxM + 1)" :key="n - 1" style="text-align: right">{{ estCalcData.fee[n -
                  1]?.toFixed(2)
                  ?? '-' }}</td>
              </tr>
              <tr>
                <td><b>预计退款金额</b></td>
                <td v-for="n in (estMaxM + 1)" :key="n - 1" style="text-align: right">{{ estCalcData.refund[n -
                  1]?.toFixed(2) ?? '-' }}</td>
              </tr>
              <tr>
                <td><b>当期业务毛利</b></td>
                <td v-for="n in (estMaxM + 1)" :key="n - 1" style="text-align: right"
                  :style="{ color: (estCalcData.margin[n - 1] ?? 0) < 0 ? 'red' : '' }">{{ estCalcData.margin[n -
                    1]?.toFixed(2) ?? '-' }}</td>
              </tr>
              <tr>
                <td><b>累计业务毛利</b></td>
                <td v-for="n in (estMaxM + 1)" :key="n - 1" style="text-align: right"
                  :style="{ color: (estCalcData.cumulativeMargin[n - 1] ?? 0) < 0 ? 'red' : '' }">{{
                    estCalcData.cumulativeMargin[n - 1]?.toFixed(2) ?? '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 回本结论 -->
        <div style="margin-top: 12px; font-size: 14px">
          <b>预估回本：</b>
          <span v-if="estBreakevenMonth != null" style="color: green">
            M{{ estBreakevenMonth }} 累计业务毛利转正（ROI≥1）
          </span>
          <span v-else style="color: red">
            M9 内尚未回本
          </span>
        </div>
      </div>
    </a-card>

    <!-- 纬度留存分析（仅管理员可见） -->
    <a-card v-if="isAdmin()" :bordered="false" class="retention-card" style="margin-top: 16px">
      <template #title>
        <span class="ltv-collapse-toggle" @click="retentionCollapsed = !retentionCollapsed">
          <CaretDownOutlined v-if="!retentionCollapsed" style="margin-right: 6px; font-size: 12px" />
          <CaretRightOutlined v-else style="margin-right: 6px; font-size: 12px" />
          纬度留存分析
        </span>
        <span class="retention-center-title">+n 留存率</span>
      </template>
      <template #extra>
        <a-radio-group v-model:value="rtHeatGroupBy" size="small" button-style="solid">
          <a-radio-button value="day">日</a-radio-button>
          <a-radio-button value="week">周</a-radio-button>
          <a-radio-button value="month">月</a-radio-button>
        </a-radio-group>
        <a-popover placement="bottomRight" trigger="hover" overlay-class-name="retention-help-popover">
          <template #title>
            <span style="font-weight: 600; font-size: 15px">数据指标说明</span>
          </template>
          <template #content>
            <div class="rh-items">
              <div class="rh-item">
                <span class="rh-label">Mn内容:</span>
                第一行:留存率, 第二行:订单数。
              </div>
              <div class="rh-item">
                <span class="rh-label">M0订单:</span>
                所选日期内的首次付费。
              </div>
              <div class="rh-item">
                <span class="rh-label">M1留存率:</span>
                所选日期的M1 59.9订单数/M0订单数。
              </div>
              <div class="rh-item">
                <span class="rh-label">M1后的留存率:</span>
                所选日期的M1后的订单数/M1订单数。
              </div>
              <div class="rh-item">
                <span class="rh-label">N周留存率:</span>
                某自然周新增订单，在该周后第N个自然周付费订单。该周后第N个自然周使用过应用的订单/该周新增订单。
              </div>
              <div class="rh-item">
                <span class="rh-label">N月留存率:</span>
                某自然月新增订单，在该月后第N个自然月付费订单。该自然月后第N个自然月中使用过应用的订单/该自然月新增订单。
              </div>
            </div>
          </template>
          <question-circle-outlined style="margin-left: 8px; color: #999; cursor: pointer" />
        </a-popover>
      </template>
      <a-spin v-show="!retentionCollapsed" :spinning="rtHeatLoading">
        <div class="retention-table-wrap">
          <table class="retention-table">
            <thead>
              <tr>
                <th class="rt-th rt-fixed">日期</th>
                <th class="rt-th">M0订单</th>
                <th class="rt-th" v-for="n in rtHeatMaxM" :key="n">M{{ n }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in rtHeatRows" :key="idx">
                <td class="rt-td rt-fixed">{{ row.label }}</td>
                <td class="rt-td">{{ row.m0Count }}</td>
                <td class="rt-td" v-for="n in rtHeatMaxM" :key="n" :style="rtHeatCellStyle(row.rates[n])">
                  <template v-if="row.rates[n] != null && row.rates[n] !== 0">
                    <div class="rt-rate">{{ row.rates[n] }}%</div>
                    <div class="rt-sub">{{ row.counts[n] ?? 0 }}</div>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </a-spin>
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { message, Modal } from "ant-design-vue";
import { hasButton, getAllowedDimensionIds, isAdmin } from "@/utils/permission";
import { analyticsUrl } from "@/utils/api";
import { get, postJson, postForm } from "@/utils/request";
import {
  QuestionCircleOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
} from "@ant-design/icons-vue";

const loading = ref(false);
const downloading = ref(false);
const resetting = ref(false);
const startDateVal = ref(null);
const endDateVal = ref(null);
const tableData = ref([]);
const dimensionOptions = ref([]);

/** 折叠状态 */
const reportCollapsed = ref(false);
const estimateCollapsed = ref(false);
const retentionCollapsed = ref(false);

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  pageSizeOptions: ["10", "20", "50", "100", "200", "300"],
  showTotal: (total) => `共 ${total} 条`,
});

const searchForm = reactive({
  startDate: "",
  endDate: "",
  dimensionId: undefined,
});

/* ---------- 上传相关 ---------- */
const showUploadModal = ref(false);
const uploading = ref(false);
const uploadDimensionId = ref(undefined);
const uploadFile = ref(null);

const handleFileChange = (info) => {
  uploadFile.value = info.file;
};

const handleUpload = async () => {
  if (!uploadDimensionId.value) {
    message.warning("请选择统计维度");
    return;
  }
  if (!uploadFile.value) {
    message.warning("请选择 xlsx 文件");
    return;
  }
  uploading.value = true;
  try {
    const formData = new FormData();
    formData.append("dimensionId", uploadDimensionId.value);
    formData.append("file", uploadFile.value.originFileObj || uploadFile.value);
    const json = await postForm(
      analyticsUrl("/order/stats/cohort/v2/daily/report/upload"),
      formData
    );
    if (json.code === 200) {
      message.success(
        `上传成功，更新了 ${json.data?.updatedCount ?? 0} 条记录`
      );
      showUploadModal.value = false;
      uploadFile.value = null;
      uploadDimensionId.value = undefined;
      fetchData(pagination.current, pagination.pageSize);
    } else {
      message.warning("上传失败：" + (json.msg || "未知错误"));
    }
  } catch {
    message.error("上传请求失败");
  } finally {
    uploading.value = false;
  }
};

/* ---------- 维度列表 ---------- */
const LTV_DIM_KEY = "ltv_v2_dimension";
const fetchDimensions = async () => {
  try {
    const json = await get(analyticsUrl("/order/stats/cohort/dimension/list"));
    const arr = json.data?.data;
    if (Array.isArray(arr)) {
      dimensionOptions.value = arr
        .filter((d) => d.isEnabled)
        .filter((d) => {
          const allowed = getAllowedDimensionIds();
          return allowed.length === 0 || allowed.includes(d.id);
        })
        .map((d) => ({ label: d.name, value: d.id }));
      // 从缓存恢复，或默认选中第一个维度
      const cached = localStorage.getItem(LTV_DIM_KEY);
      const cachedId = cached != null ? Number(cached) : null;
      if (
        cachedId &&
        dimensionOptions.value.some((o) => o.value === cachedId)
      ) {
        searchForm.dimensionId = cachedId;
      } else if (
        searchForm.dimensionId == null &&
        dimensionOptions.value.length > 0
      ) {
        searchForm.dimensionId = dimensionOptions.value[0].value;
      }
    }
  } catch {
    // 静默
  }
};

/* ---------- 格式化工具 ---------- */
const formatAmount = (fen) => {
  if (fen == null) return "-";
  return (
    "¥" +
    (fen / 100).toLocaleString("zh-CN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

const formatRate = (rate) => {
  if (rate == null) return "-";
  const val = parseFloat(rate);
  if (isNaN(val)) return "-";
  return (val * 100).toFixed(2) + "%";
};

const formatDecimal = (val) => {
  if (val == null) return "-";
  const v = parseFloat(val);
  if (isNaN(v)) return "-";
  return v.toFixed(2);
};

const nowrap = { style: { whiteSpace: "nowrap" } };

const getStage = (stages, mNo) =>
  (stages ?? []).find((s) => s.mNo === mNo) ?? null;

const pageMaxMNo = computed(() =>
  tableData.value.reduce((max, r) => Math.max(max, r.maxMNo ?? 0), 0)
);

/* ---------- 列定义 ---------- */
const columns = computed(() => {
  const base = [
    {
      title: "首购日期",
      dataIndex: "statDate",
      key: "statDate",
      width: 110,
      fixed: "left",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
    },
    {
      title: "维度",
      dataIndex: "dimensionName",
      key: "dimensionName",
      width: 150,
      fixed: "left",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
    },
    {
      title: "当日利润",
      dataIndex: "dayProfit",
      key: "dayProfit",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "当日ROI",
      dataIndex: "dayRoi",
      key: "dayRoi",
      width: 100,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatDecimal(text),
    },
    {
      title: "总利润",
      dataIndex: "totalProfit",
      key: "totalProfit",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "总ROI",
      dataIndex: "totalRoi",
      key: "totalRoi",
      width: 100,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatDecimal(text),
    },
    {
      title: "ROI=1",
      key: "roiBreakeven",
      width: 80,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ record }) => {
        const roi = parseFloat(record.totalRoi);
        if (isNaN(roi)) return "-";
        return roi >= 1 ? "✓" : "✗";
      },
    },
    {
      title: "账显消耗",
      dataIndex: "displayedCost",
      key: "displayedCost",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "现金消耗",
      dataIndex: "cashCost",
      key: "cashCost",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "后端成本",
      dataIndex: "backendCost",
      key: "backendCost",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },

    {
      title: "总订单",
      dataIndex: "m0OrderCount",
      key: "totalOrderCount",
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    }, {
      title: "展示",
      dataIndex: "impressionCount",
      key: "impressionCount",
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "平均千次展现费用(元)",
      dataIndex: "avgCpm",
      key: "avgCpm",
      width: 160,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatDecimal(text),
    },
    {
      title: "点击",
      dataIndex: "clickCount",
      key: "clickCount",
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "平均点击单价(元)",
      dataIndex: "avgCpc",
      key: "avgCpc",
      width: 140,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatDecimal(text),
    },
    {
      title: "点击率(%)",
      dataIndex: "ctr",
      key: "ctr",
      width: 100,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatRate(text),
    },
    {
      title: "转化数",
      dataIndex: "conversionCount",
      key: "conversionCount",
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "转化成本",
      dataIndex: "conversionCost",
      key: "conversionCost",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatDecimal(text),
    },
    {
      title: "M0订单数",
      dataIndex: "m0OrderCount",
      key: "m0OrderCount",
      width: 100,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "M0订单收入",
      dataIndex: "m0OrderAmount",
      key: "m0OrderAmount",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    /* M1 固定列 */
    {
      title: "M1订单数",
      key: "m1Count",
      width: 100,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ record }) => {
        const s = getStage(record.stages, 1);
        return s != null ? s.orderCount.toLocaleString() : "-";
      },
    },
    {
      title: "M1获客成本",
      dataIndex: "m1AcquisitionCost",
      key: "m1AcquisitionCost",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "M1收入",
      key: "m1Amount",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ record }) => {
        const s = getStage(record.stages, 1);
        return s != null ? formatAmount(s.orderAmount) : "-";
      },
    },
    {
      title: "M1留存率",
      key: "m1Rate",
      width: 100,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ record }) => {
        const s = getStage(record.stages, 1);
        return s != null ? formatRate(s.retentionRate) : "-";
      },
    },
  ];

  /* M2 ~ Mn 动态列 */
  const max = pageMaxMNo.value;
  const stageCols = [];
  for (let n = 2; n <= max; n++) {
    const mn = n;
    stageCols.push(
      {
        title: `M${mn}订单数`,
        key: `m${mn}Count`,
        width: 100,
        align: "center",
        customCell: () => nowrap,
        customHeaderCell: () => nowrap,
        customRender: ({ record }) => {
          const s = getStage(record.stages, mn);
          return s != null ? s.orderCount.toLocaleString() : "-";
        },
      },
      {
        title: `M${mn}收入`,
        key: `m${mn}Amount`,
        width: 120,
        align: "center",
        customCell: () => nowrap,
        customHeaderCell: () => nowrap,
        customRender: ({ record }) => {
          const s = getStage(record.stages, mn);
          return s != null ? formatAmount(s.orderAmount) : "-";
        },
      },
      {
        title: `M${mn}留存率`,
        key: `m${mn}Rate`,
        width: 100,
        align: "center",
        customCell: () => nowrap,
        customHeaderCell: () => nowrap,
        customRender: ({ record }) => {
          const s = getStage(record.stages, mn);
          return s != null ? formatRate(s.retentionRate) : "-";
        },
      }
    );
  }

  /* 尾部固定列 */
  const tail = [
    {
      title: "手续费",
      dataIndex: "serviceFee",
      key: "serviceFee",
      width: 100,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "预估应支付通道费",
      dataIndex: "estimatedChannelFee",
      key: "estimatedChannelFee",
      width: 140,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "快钱收款手续费",
      dataIndex: "collectFee",
      key: "collectFee",
      width: 130,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "预计快钱退款手续费",
      dataIndex: "refundFee",
      key: "refundFee",
      width: 150,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "退款数",
      dataIndex: "refundOrderCount",
      key: "refundOrderCount",
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "退款金额",
      dataIndex: "refundAmount",
      key: "refundAmount",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "线下退款单数",
      dataIndex: "offlineRefundCount",
      key: "offlineRefundCount",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "线下退款金额",
      dataIndex: "offlineRefundAmount",
      key: "offlineRefundAmount",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "退款比例",
      dataIndex: "refundRate",
      key: "refundRate",
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatRate(text),
    },
    {
      title: "客服费用",
      dataIndex: "customerServiceFee",
      key: "customerServiceFee",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "使用快钱余额充值",
      dataIndex: "kqRechargeAmount",
      key: "kqRechargeAmount",
      width: 140,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "回款金额",
      dataIndex: "receivedAmount",
      key: "receivedAmount",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "巨量退回余额",
      dataIndex: "jlReturnBalance",
      key: "jlReturnBalance",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "利润分成",
      dataIndex: "profitSharing",
      key: "profitSharing",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "未充值金额",
      dataIndex: "notRechargeAmount",
      key: "notRechargeAmount",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "账户余额",
      dataIndex: "accountBalance",
      key: "accountBalance",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 160,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
    },
  ];

  return [...base, ...stageCols, ...tail];
});

/* ---------- 当页汇总统计 ---------- */
const sumFields = [
  "displayedCost",
  "cashCost",
  "impressionCount",
  "clickCount",
  "conversionCount",
  "m0OrderCount",
  "m0OrderAmount",
  "totalOrderAmount",
  "serviceFee",
  "estimatedChannelFee",
  "collectFee",
  "refundFee",
  "refundOrderCount",
  "refundAmount",
  "offlineRefundCount",
  "offlineRefundAmount",
  "customerServiceFee",
  "kqRechargeAmount",
  "receivedAmount",
  "jlReturnBalance",
  "profitSharing",
  "notRechargeAmount",
];

/** 汇总各 M 阶段的订单数和金额 */
const stageSums = computed(() => {
  const max = pageMaxMNo.value;
  const result = {};
  for (let n = 0; n <= max; n++) {
    let countSum = 0;
    let amountSum = 0;
    for (const r of tableData.value) {
      const s = getStage(r.stages, n);
      if (s) {
        countSum += s.orderCount ?? 0;
        amountSum += s.orderAmount ?? 0;
      }
    }
    result[n] = { count: countSum, amount: amountSum };
  }
  return result;
});

/** ΣM阶段收入 = 所有 stages 金额之和 */
const totalStageAmount = computed(() => {
  const ss = stageSums.value;
  let total = 0;
  for (const n in ss) total += ss[n].amount;
  return total;
});

const summaryData = computed(() => {
  const sums = {};
  for (const k of sumFields) sums[k] = 0;
  for (const r of tableData.value) {
    for (const k of sumFields) sums[k] += r[k] ?? 0;
  }
  return sums;
});

const summaryCells = computed(() => {
  const sums = summaryData.value;
  const ss = stageSums.value;
  const tsa = totalStageAmount.value;
  const m1Count = ss[1]?.count ?? 0;
  const m1Amount = ss[1]?.amount ?? 0;

  /* 预计算公式中的公共部分:
     净收入部分 = ΣM阶段收入 - 预估通道费 - 收款手续费 + 退款手续费 - 退款金额 - 线下退款金额 - 客服费用 */
  const netRevenue =
    tsa -
    sums.estimatedChannelFee -
    sums.collectFee +
    sums.refundFee -
    sums.refundAmount -
    sums.offlineRefundAmount -
    sums.customerServiceFee;

  /* 合计行的各计算字段 */
  const dayProfit = sums.m0OrderAmount - sums.cashCost;
  const dayRoi =
    sums.cashCost !== 0 ? sums.m0OrderAmount / sums.cashCost : null;
  const totalProfit = netRevenue - sums.cashCost;
  const totalRoi = sums.cashCost !== 0 ? netRevenue / sums.cashCost : null;
  const roiBreakeven = totalRoi != null ? (totalRoi >= 1 ? "✓" : "✗") : "-";
  const avgCpm =
    sums.impressionCount !== 0
      ? ((sums.displayedCost / sums.impressionCount) * 1000) / 100
      : null;
  const avgCpc =
    sums.clickCount !== 0 ? sums.displayedCost / sums.clickCount / 100 : null;
  const ctr =
    sums.impressionCount !== 0 ? sums.clickCount / sums.impressionCount : null;
  const conversionCost =
    sums.conversionCount !== 0
      ? sums.displayedCost / sums.conversionCount / 100
      : null;
  const backendCost =
    sums.m0OrderCount !== 0
      ? Math.round(sums.cashCost / sums.m0OrderCount)
      : null;
  const m1AcquisitionCost =
    m1Count !== 0 ? Math.round(sums.cashCost / m1Count) : null;
  const refundRate =
    sums.totalOrderAmount !== 0
      ? (sums.refundAmount + sums.offlineRefundAmount) / sums.totalOrderAmount
      : null;
  /* 合计行账户余额: 在原规则基础上, 额外加上未充值金额(notRechargeAmount) */
  const accountBalance =
    netRevenue -
    sums.receivedAmount +
    sums.jlReturnBalance -
    sums.kqRechargeAmount -
    sums.profitSharing +
    sums.notRechargeAmount;

  /* 前两列: 首购日期(合计) + 维度(空) */
  const cells = ["合计", ""];

  /* base 列（与 columns base 定义一一对应，跳过前两个固定列） */
  cells.push(formatAmount(dayProfit));
  cells.push(dayRoi != null ? dayRoi.toFixed(2) : "-");
  cells.push(formatAmount(totalProfit));
  cells.push(totalRoi != null ? totalRoi.toFixed(2) : "-");
  cells.push(roiBreakeven);
  cells.push(formatAmount(sums.displayedCost));
  cells.push(formatAmount(sums.cashCost));
  cells.push(sums.impressionCount.toLocaleString());
  cells.push(avgCpm != null ? avgCpm.toFixed(2) : "-");
  cells.push(sums.clickCount.toLocaleString());
  cells.push(avgCpc != null ? avgCpc.toFixed(2) : "-");
  cells.push(ctr != null ? (ctr * 100).toFixed(2) + "%" : "-");
  cells.push(sums.conversionCount.toLocaleString());
  cells.push(conversionCost != null ? conversionCost.toFixed(2) : "-");
  cells.push(sums.m0OrderCount.toLocaleString()); /* 总订单 = m0OrderCount */
  cells.push(backendCost != null ? formatAmount(backendCost) : "-");
  cells.push(sums.m0OrderCount.toLocaleString()); /* M0订单数 */
  cells.push(formatAmount(sums.m0OrderAmount));
  /* M1 固定列 */
  cells.push(m1Count.toLocaleString());
  cells.push(m1AcquisitionCost != null ? formatAmount(m1AcquisitionCost) : "-");
  cells.push(formatAmount(m1Amount));
  /* M1留存率 = M1订单数 / M0订单数 */
  const m1Retention =
    sums.m0OrderCount !== 0 ? m1Count / sums.m0OrderCount : null;
  cells.push(m1Retention != null ? (m1Retention * 100).toFixed(2) + "%" : "-");

  /* M2 ~ Mn 动态阶段列汇总 */
  const max = pageMaxMNo.value;
  for (let n = 2; n <= max; n++) {
    const stg = ss[n] ?? { count: 0, amount: 0 };
    cells.push(stg.count.toLocaleString());
    cells.push(formatAmount(stg.amount));
    /* M2+ 留存率 = 当前阶段订单数 / M1订单数 */
    const ret = m1Count !== 0 ? stg.count / m1Count : null;
    cells.push(ret != null ? (ret * 100).toFixed(2) + "%" : "-");
  }

  /* tail 列 */
  cells.push(formatAmount(sums.serviceFee));
  cells.push(formatAmount(sums.estimatedChannelFee));
  cells.push(formatAmount(sums.collectFee));
  cells.push(formatAmount(sums.refundFee));
  cells.push(sums.refundOrderCount?.toLocaleString() ?? "-");
  cells.push(formatAmount(sums.refundAmount));
  cells.push(sums.offlineRefundCount?.toLocaleString() ?? "-");
  cells.push(formatAmount(sums.offlineRefundAmount));
  cells.push(refundRate != null ? (refundRate * 100).toFixed(2) + "%" : "-");
  cells.push(formatAmount(sums.customerServiceFee));
  cells.push(formatAmount(sums.kqRechargeAmount));
  cells.push(formatAmount(sums.receivedAmount));
  cells.push(formatAmount(sums.jlReturnBalance));
  cells.push(formatAmount(sums.profitSharing));
  cells.push(formatAmount(sums.notRechargeAmount));
  cells.push(formatAmount(accountBalance));
  cells.push(""); // 更新时间（合计行不汇总）

  return cells;
});

/* ---------- 数据获取 ---------- */
const fetchData = async (pageIdx = 1, pageSize = 20) => {
  loading.value = true;
  try {
    const params = {
      pageIdx,
      pageSize,
      startDate: searchForm.startDate || "",
      endDate: searchForm.endDate || "",
    };
    if (searchForm.dimensionId != null)
      params.dimensionId = searchForm.dimensionId;
    const json = await get(
      analyticsUrl("/order/stats/cohort/v2/daily/report"),
      params
    );
    if (json.code === 200 && json.data) {
      const d = json.data;
      tableData.value = d.data.map((r, i) => ({
        ...r,
        rowKey: `${r.statDate}_${r.dimensionId}_${i}`,
      }));
      pagination.total = d.dataSum;
      pagination.current = d.pageIdx;
      pagination.pageSize = d.pageSize;
    } else {
      message.warning("查询失败：" + (json.msg || json.content || "未知错误"));
    }
  } catch {
    message.error("接口请求失败");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  searchForm.startDate = startDateVal.value || "";
  searchForm.endDate = endDateVal.value || "";
  if (searchForm.dimensionId != null) {
    pagination.pageSize = 300;
    localStorage.setItem(LTV_DIM_KEY, String(searchForm.dimensionId));
  }
  fetchData(1, pagination.pageSize);
  if (isAdmin()) {
    fetchRtHeatData();
  }
};

const handleReset = () => {
  startDateVal.value = null;
  endDateVal.value = null;
  searchForm.startDate = "";
  searchForm.endDate = "";
  searchForm.dimensionId =
    dimensionOptions.value.length > 0
      ? dimensionOptions.value[0].value
      : undefined;
  if (searchForm.dimensionId != null)
    localStorage.setItem(LTV_DIM_KEY, String(searchForm.dimensionId));
  fetchData(1, pagination.pageSize);
  if (isAdmin()) {
    fetchRtHeatData();
  }
};

const handleTableChange = (pag) => {
  fetchData(pag.current, pag.pageSize);
};

const handleResetUpload = () => {
  if (searchForm.dimensionId == null) {
    message.warning("请先选择统计维度");
    return;
  }
  const dimName =
    dimensionOptions.value.find((d) => d.value === searchForm.dimensionId)
      ?.label ?? searchForm.dimensionId;
  Modal.confirm({
    title: "确认重置上传字段",
    content: `将重置维度「${dimName}」${searchForm.startDate && searchForm.endDate
      ? `（${searchForm.startDate} ~ ${searchForm.endDate}）`
      : "（全部日期）"
      }的人工录入数据为 0，此操作不可撤销，是否继续？`,
    okText: "确认重置",
    okType: "danger",
    cancelText: "取消",
    async onOk() {
      resetting.value = true;
      try {
        const params = { dimensionId: searchForm.dimensionId };
        if (searchForm.startDate) params.startDate = searchForm.startDate;
        if (searchForm.endDate) params.endDate = searchForm.endDate;
        const json = await postJson(
          analyticsUrl("/order/stats/cohort/v2/daily/report/reset"),
          params
        );
        if (json.code === 200) {
          message.success("重置成功");
          fetchData(1, pagination.pageSize);
        } else {
          message.warning("重置失败：" + (json.msg || "未知错误"));
        }
      } catch {
        message.error("重置请求失败");
      } finally {
        resetting.value = false;
      }
    },
  });
};

/* ---------- CSV 下载 ---------- */
const handleDownload = async () => {
  downloading.value = true;
  try {
    const allData = [];
    let pageIdx = 1;
    let pageCount = 1;
    let globalMaxMNo = 0;
    while (pageIdx <= pageCount) {
      const params = {
        pageIdx,
        pageSize: 300,
        startDate: searchForm.startDate || "",
        endDate: searchForm.endDate || "",
      };
      if (searchForm.dimensionId != null)
        params.dimensionId = searchForm.dimensionId;
      const json = await get(
        analyticsUrl("/order/stats/cohort/v2/daily/report"),
        params
      );
      if (json.code !== 200 || !json.data) break;
      allData.push(...json.data.data);
      for (const r of json.data.data)
        globalMaxMNo = Math.max(globalMaxMNo, r.maxMNo ?? 0);
      pageCount = json.data.pageCount;
      pageIdx++;
    }
    if (!allData.length) {
      message.warning("没有数据可下载");
      return;
    }

    const amountFields = [
      "dayProfit",
      "totalProfit",
      "displayedCost",
      "cashCost",
      "backendCost",
      "m0OrderAmount",
      "m1AcquisitionCost",
      "serviceFee",
      "estimatedChannelFee",
      "collectFee",
      "refundFee",
      "refundAmount",
      "offlineRefundAmount",
      "customerServiceFee",
      "kqRechargeAmount",
      "receivedAmount",
      "jlReturnBalance",
      "profitSharing",
      "notRechargeAmount",
      "accountBalance",
    ];
    const rateFields = ["refundRate", "ctr"];
    const decimalFields = [
      "dayRoi",
      "totalRoi",
      "avgCpm",
      "avgCpc",
      "conversionCost",
    ];

    const baseHeaders = [
      "首购日期",
      "维度",
      "当日利润",
      "当日ROI",
      "总利润",
      "总ROI",
      "ROI=1",
      "账显消耗",
      "现金消耗",
      "后端成本",
      "总订单",
      "展示",
      "平均千次展现费用(元)",
      "点击",
      "平均点击单价(元)",
      "点击率(%)",
      "转化数",
      "转化成本",
      "M0订单数",
      "M0订单收入",
      "M1订单数",
      "M1获客成本",
      "M1收入",
      "M1留存率",
    ];
    const baseKeys = [
      "statDate",
      "dimensionName",
      "dayProfit",
      "dayRoi",
      "totalProfit",
      "totalRoi",
      "_roiBreakeven",
      "displayedCost",
      "cashCost",
      "impressionCount",
      "avgCpm",
      "clickCount",
      "avgCpc",
      "ctr",
      "conversionCount",
      "conversionCost",
      "m0OrderCount",
      "backendCost",
      "m0OrderCount",
      "m0OrderAmount",
      "_m1Count",
      "m1AcquisitionCost",
      "_m1Amount",
      "_m1Rate",
    ];

    const tailHeaders = [
      "手续费",
      "预估应支付通道费",
      "快钱收款手续费",
      "预计快钱退款手续费",
      "退款数",
      "退款金额",
      "线下退款单数",
      "线下退款金额",
      "退款比例",
      "客服费用",
      "使用快钱余额充值",
      "回款金额",
      "巨量退回余额",
      "利润分成",
      "未充值金额",
      "账户余额",
      "更新时间",
    ];
    const tailKeys = [
      "serviceFee",
      "estimatedChannelFee",
      "collectFee",
      "refundFee",
      "refundOrderCount",
      "refundAmount",
      "offlineRefundCount",
      "offlineRefundAmount",
      "refundRate",
      "customerServiceFee",
      "kqRechargeAmount",
      "receivedAmount",
      "jlReturnBalance",
      "profitSharing",
      "notRechargeAmount",
      "accountBalance",
      "updatedAt",
    ];

    const stageHeaders = [];
    for (let n = 2; n <= globalMaxMNo; n++) {
      stageHeaders.push(`M${n}订单数`, `M${n}收入`, `M${n}留存率`);
    }

    const esc = (v) => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n")
        ? '"' + s.replace(/"/g, '""') + '"'
        : s;
    };

    const header = [...baseHeaders, ...stageHeaders, ...tailHeaders]
      .map(esc)
      .join(",");
    const rows = allData.map((r) => {
      const baseCells = baseKeys.map((k) => {
        if (k === "_roiBreakeven") {
          const roi = parseFloat(r.totalRoi);
          return isNaN(roi) ? "" : roi >= 1 ? "✓" : "✗";
        }
        if (k === "_m1Count") {
          const s = getStage(r.stages, 1);
          return s ? s.orderCount : "";
        }
        if (k === "_m1Amount") {
          const s = getStage(r.stages, 1);
          return s ? (s.orderAmount / 100).toFixed(2) : "";
        }
        if (k === "_m1Rate") {
          const s = getStage(r.stages, 1);
          if (!s || s.retentionRate == null) return "";
          const val = parseFloat(s.retentionRate);
          return isNaN(val) ? "" : (val * 100).toFixed(2) + "%";
        }
        const v = r[k];
        if (v == null) return "";
        if (amountFields.includes(k)) return (v / 100).toFixed(2);
        if (rateFields.includes(k)) {
          const val = parseFloat(v);
          return isNaN(val) ? "" : (val * 100).toFixed(2) + "%";
        }
        if (decimalFields.includes(k)) {
          const val = parseFloat(v);
          return isNaN(val) ? "" : val.toFixed(2);
        }
        return v;
      });
      const stageCells = [];
      for (let n = 2; n <= globalMaxMNo; n++) {
        const s = getStage(r.stages, n);
        if (s) {
          stageCells.push(
            s.orderCount,
            (s.orderAmount / 100).toFixed(2),
            s.retentionRate != null
              ? (parseFloat(s.retentionRate) * 100).toFixed(2) + "%"
              : ""
          );
        } else {
          stageCells.push("", "", "");
        }
      }
      const tailCells = tailKeys.map((k) => {
        const v = r[k];
        if (v == null) return "";
        if (amountFields.includes(k)) return (v / 100).toFixed(2);
        if (rateFields.includes(k)) {
          const val = parseFloat(v);
          return isNaN(val) ? "" : (val * 100).toFixed(2) + "%";
        }
        return v;
      });
      return [...baseCells, ...stageCells, ...tailCells].map(esc).join(",");
    });
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "LTV报告V2.csv";
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    message.error("下载失败");
  } finally {
    downloading.value = false;
  }
};

/* ---------- 维度管理 ---------- */
const showDimModal = ref(false);
const dimLoading = ref(false);
const dimSaving = ref(false);
const dimList = ref([]);
const dimEditingId = ref(null);
const dimEditForm = reactive({ name: "", sort: 0, isEnabled: true });

const dimColumns = [
  { title: "ID", dataIndex: "id", key: "id", width: 60 },
  { title: "编码", dataIndex: "code", key: "code", width: 140 },
  { title: "名称", key: "name", width: 180 },
  { title: "排序", key: "sort", width: 80 },
  { title: "状态", key: "isEnabled", width: 80 },
  { title: "操作", key: "action", width: 120 },
];

const openDimModal = async () => {
  showDimModal.value = true;
  dimEditingId.value = null;
  dimLoading.value = true;
  try {
    const json = await get(analyticsUrl("/order/stats/cohort/dimension/list"));
    if (json.code === 200) {
      dimList.value = json.data?.data || json.data || [];
    } else {
      message.error(json.msg || "获取维度列表失败");
    }
  } catch {
    message.error("获取维度列表失败");
  } finally {
    dimLoading.value = false;
  }
};

const startDimEdit = (record) => {
  dimEditingId.value = record.id;
  dimEditForm.name = record.name;
  dimEditForm.sort = record.sort;
  dimEditForm.isEnabled = !!record.isEnabled;
};

const handleDimSave = (record) => {
  Modal.confirm({
    title: "确认修改",
    content: `确定要保存对维度「${dimEditForm.name}」的修改吗？`,
    okText: "确定",
    cancelText: "取消",
    async onOk() {
      dimSaving.value = true;
      try {
        const body = {
          id: record.id,
          name: dimEditForm.name,
          sort: dimEditForm.sort,
          isEnabled: dimEditForm.isEnabled,
        };
        const json = await postJson(
          analyticsUrl("/order/stats/cohort/dimension/update"),
          body
        );
        if (json.code === 200) {
          message.success("修改成功");
          dimEditingId.value = null;
          // 刷新维度列表
          await openDimModal();
          // 同步刷新搜索栏维度下拉
          await fetchDimensions();
        } else {
          message.error(json.msg || "修改失败");
        }
      } catch {
        message.error("修改失败");
      } finally {
        dimSaving.value = false;
      }
    },
  });
};

/* ---------- 预估回本模块 ---------- */
/** 预估回本最大M值，跟随维度表 */
const estMaxM = computed(() => Math.max(pageMaxMNo.value, 1));
/** 留存率加载/保存状态 */
const retentionLoading = ref(false);
const retentionSaving = ref(false);
/** 手续费率：维度名含 hlb 时 4%，否则 15% */
const estFeeRate = computed(() => {
  const dimId = searchForm.dimensionId;
  if (dimId == null) return 0.15;
  const dimName = (
    dimensionOptions.value.find((d) => d.value === dimId)?.label ?? ""
  ).toLowerCase();
  return dimName.includes("hlb") ? 0.04 : 0.15;
});
/** 订单分成收入单价（元），可编辑 */
const DEFAULT_EST_UNIT_PRICE = 59.9;
const estUnitPrice = ref(DEFAULT_EST_UNIT_PRICE);
const estEffectiveUnitPrice = computed(() => {
  const raw = estUnitPrice.value;
  if (raw == null || raw === "") return DEFAULT_EST_UNIT_PRICE;
  const value = Number(raw);
  return Number.isFinite(value) ? value : DEFAULT_EST_UNIT_PRICE;
});

const estEffectiveUnitPriceDisplay = computed(() =>
  estEffectiveUnitPrice.value.toFixed(2).replace(/\.?0+$/, "")
);
/** 投放首次价格（元），可编辑 */
const estFirstPrice = ref(29.9);
/** 原始获客成本（元），可编辑 */
const estAcquisitionCost = ref(0);

/**
 * 留存率表 — 从接口获取，可手动修改
 * 值为小数形式 (如 0.95 表示 95%)
 */
const estRetentionRates = reactive({});

/** 从接口加载留存率 */
const fetchRetentionRates = async () => {
  const dimId = searchForm.dimensionId;
  if (dimId == null) {
    message.warning("请先选择统计维度");
    return;
  }
  retentionLoading.value = true;
  try {
    const json = await get(
      analyticsUrl("/order/stats/cohort/retention-rate/query"),
      { dimensionId: dimId }
    );
    if (json.code === 200 && json.data) {
      const d = json.data;
      // m0~m15 映射到 estRetentionRates[0]~[15]，接口值为百分比数值，需转为小数
      for (let i = 0; i <= 15; i++) {
        const val = d[`m${i}`];
        estRetentionRates[i] = val != null ? val / 100 : null;
      }
      message.success("留存率已加载");
    } else if (json.code === 200 && json.data === null) {
      message.info("该维度尚未设置留存率");
    } else {
      message.warning(
        "获取留存率失败：" + (json.errMsg || json.msg || "未知错误")
      );
    }
  } catch {
    message.error("获取留存率请求失败");
  } finally {
    retentionLoading.value = false;
  }
};

/** 保存留存率到接口 */
const handleSaveRetentionRates = async () => {
  const dimId = searchForm.dimensionId;
  if (dimId == null) {
    message.warning("请先选择统计维度");
    return;
  }
  retentionSaving.value = true;
  try {
    const body = { dimensionId: dimId };
    for (let i = 0; i <= 15; i++) {
      const v = estRetentionRates[i];
      if (v != null) {
        body[`m${i}`] = +(v * 100).toFixed(2);
      }
    }
    const json = await postJson(
      analyticsUrl("/order/stats/cohort/retention-rate/update"),
      body
    );
    if (json.code === 200) {
      message.success("留存率已保存");
    } else {
      message.warning(
        "保存留存率失败：" + (json.errMsg || json.msg || "未知错误")
      );
    }
  } catch {
    message.error("保存留存率请求失败");
  } finally {
    retentionSaving.value = false;
  }
};

/** M1留存率（百分比数值，如 50 表示 50%），可手动修改，默认自动计算 */
const estM1Retention = ref(null);

/** 自动计算 M1 留存率 */
const calcM1Retention = () => {
  const ss = stageSums.value;
  const m0Count = ss[0]?.count ?? 0;
  const m1Count = ss[1]?.count ?? 0;
  if (m0Count === 0) return null;
  return +((m1Count / m0Count) * 100).toFixed(2);
};

/** 重置 M1 留存率为自动计算值 */
const resetM1Retention = () => {
  estM1Retention.value = calcM1Retention();
};

/** 退款率（百分比数值，如 5 表示 5%），可手动修改，默认使用合计退款比例 */
const estRefundRate = ref(null);

/** 自动计算退款率（基于合计行数据） */
const calcRefundRate = () => {
  const sums = summaryData.value;
  const totalAmount = sums.totalOrderAmount ?? 0;
  if (totalAmount === 0) return null;
  return +(
    ((sums.refundAmount + sums.offlineRefundAmount) / totalAmount) *
    100
  ).toFixed(2);
};

/** 重置退款率为自动计算值 */
const resetRefundRate = () => {
  estRefundRate.value = calcRefundRate();
};

/** 有效单价获客成本 = 原始获客成本 / (M1留存率/100) */
const estAdjustedCost = computed(() => {
  const m1Ret = estM1Retention.value;
  if (m1Ret == null || m1Ret === 0) return null;
  return estAcquisitionCost.value / (m1Ret / 100);
});

const estAdjustedCostDisplay = computed(() => {
  const v = estAdjustedCost.value;
  return v != null ? v.toFixed(2) + " 元" : "-";
});

/** 实时计算表格（M1~M9） */
const estCalcData = computed(() => {
  const rates = estRetentionRates;
  const refundPct = (estRefundRate.value ?? 0) / 100;
  const revenue = {};
  const cumulativeRevenue = {};
  const fee = {};
  const refund = {};
  const margin = {};
  const cumulativeMargin = {};

  const max = estMaxM.value;

  // M0: 投放首次价格相关
  revenue[0] = estFirstPrice.value;
  cumulativeRevenue[0] = revenue[0];
  fee[0] = revenue[0] * estFeeRate.value;
  refund[0] = revenue[0] * refundPct;
  margin[0] = revenue[0] - fee[0] - refund[0] - estAcquisitionCost.value;
  cumulativeMargin[0] = margin[0];

  // M1~Mn
  for (let n = 1; n <= max; n++) {
    const r = rates[n];
    if (r == null) {
      revenue[n] = 0;
    } else {
      // Mn收入 = 订单分成收入单价 * Mn留存率
      revenue[n] = estEffectiveUnitPrice.value * r;
    }

    // 累计收入
    cumulativeRevenue[n] = cumulativeRevenue[n - 1] + revenue[n];

    // 通道+提现服务费 = 收入 * 手续费率
    fee[n] = revenue[n] * estFeeRate.value;

    // 预计退款金额 = 收入 * 退款率
    refund[n] = revenue[n] * refundPct;

    // 当期业务毛利
    margin[n] = revenue[n] - fee[n] - refund[n];

    // 累计业务毛利
    cumulativeMargin[n] = cumulativeMargin[n - 1] + margin[n];
  }

  return { revenue, cumulativeRevenue, fee, refund, margin, cumulativeMargin };
});

/** 回本月份：累计业务毛利首次 >= 0 的 M */
const estBreakevenMonth = computed(() => {
  const cm = estCalcData.value.cumulativeMargin;
  const max = estMaxM.value;
  for (let n = 0; n <= max; n++) {
    if ((cm[n] ?? -1) >= 0) return n;
  }
  return null;
});

// ========================
// 留存率热力表（仅管理员）
// ========================

const rtHeatLoading = ref(false);
const RETENTION_GROUP_KEY = "ltv_retention_group";
const rtHeatRawData = ref([]);
const rtHeatGroupBy = ref(localStorage.getItem(RETENTION_GROUP_KEY) || "day");

const rtHeatMaxM = computed(() =>
  rtHeatRawData.value.reduce(
    (max, r) => Math.max(max, ...(r.stages ?? []).map((s) => s.mNo)),
    0
  )
);

const getMonday = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
};

const rtHeatDailyRows = computed(() =>
  [...rtHeatRawData.value].reverse().map((r) => {
    const counts = {};
    const rates = {};
    for (const s of r.stages ?? []) {
      if (s.mNo >= 1) {
        counts[s.mNo] = s.orderCount ?? 0;
        const val = parseFloat(s.retentionRate);
        rates[s.mNo] = isNaN(val) ? null : +(val * 100).toFixed(1);
      }
    }
    return {
      statDate: r.statDate,
      label: r.statDate,
      m0Count: r.m0OrderCount ?? 0,
      counts,
      rates,
    };
  })
);

const retentionGroupRows = (rows, keyFn, labelFn) => {
  const map = new Map();
  for (const r of rows) {
    const k = keyFn(r.statDate);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  }
  const result = [];
  for (const [, group] of map) {
    const m0Sum = group.reduce((s, r) => s + r.m0Count, 0);
    const maxN = rtHeatMaxM.value;
    const counts = {};
    const rates = {};
    for (let n = 1; n <= maxN; n++) {
      const cntSum = group.reduce((s, r) => s + (r.counts[n] ?? 0), 0);
      counts[n] = cntSum;
      const hasData = group.some((r) => r.counts[n] != null);
      // 周/月留存率均以 M0 订单数为分母
      const base = m0Sum;
      rates[n] =
        hasData && base > 0 ? +((cntSum / base) * 100).toFixed(1) : null;
    }
    result.push({
      label: labelFn(group),
      m0Count: m0Sum,
      counts,
      rates,
    });
  }
  return result;
};

const rtHeatRows = computed(() => {
  const daily = rtHeatDailyRows.value;
  let rows;
  if (rtHeatGroupBy.value === "week") {
    rows = retentionGroupRows(
      daily,
      (d) => getMonday(d),
      (g) => {
        const mon = getMonday(g[0].statDate);
        const sun = new Date(mon);
        sun.setDate(sun.getDate() + 6);
        const sunStr = sun.toISOString().slice(0, 10);
        return mon + "~" + sunStr;
      }
    );
  } else if (rtHeatGroupBy.value === "month") {
    rows = retentionGroupRows(
      daily,
      (d) => d.slice(0, 7),
      (g) => g[0].statDate.slice(0, 7)
    );
  } else {
    rows = daily;
  }
  return rows.filter(
    (r) =>
      r.m0Count > 0 || Object.values(r.rates).some((v) => v != null && v !== 0)
  );
});

const rtHeatCellStyle = (rate) => {
  if (rate == null || rate === 0) return {};
  const alpha = Math.min(0.05 + (rate / 100) * 0.55, 0.65);
  const textColor = alpha > 0.35 ? "#fff" : "#333";
  return {
    background: `rgba(24, 144, 255, ${alpha})`,
    color: textColor,
  };
};

const fetchRtHeatData = async () => {
  rtHeatLoading.value = true;
  try {
    const params = {
      pageIdx: 1,
      pageSize: 300,
      startDate: searchForm.startDate || "",
      endDate: searchForm.endDate || "",
    };
    if (searchForm.dimensionId != null)
      params.dimensionId = searchForm.dimensionId;
    const json = await get(
      analyticsUrl("/order/stats/cohort/v2/daily/report"),
      params
    );
    if (json.code === 200 && json.data) {
      rtHeatRawData.value = json.data.data ?? [];
    }
  } catch {
    message.error("留存数据加载失败");
  } finally {
    rtHeatLoading.value = false;
  }
};

watch(rtHeatGroupBy, (v) => {
  localStorage.setItem(RETENTION_GROUP_KEY, v);
});

/* ---------- 初始化 ---------- */
onMounted(async () => {
  await fetchDimensions();
  fetchData();
  if (isAdmin()) fetchRtHeatData();
});

/** 数据加载后自动计算 M1 留存率和退款率（仅当用户未手动修改时） */
watch(
  () => stageSums.value,
  () => {
    if (estM1Retention.value == null) {
      estM1Retention.value = calcM1Retention();
    }
  },
  { deep: true }
);

watch(
  () => summaryData.value,
  () => {
    if (estRefundRate.value == null) {
      estRefundRate.value = calcRefundRate();
    }
  },
  { deep: true }
);

/** 维度切换时自动加载留存率 */
watch(
  () => searchForm.dimensionId,
  (dimId) => {
    if (dimId != null) {
      fetchRetentionRates();
    }
  }
);
</script>

<style scoped>
.ltv-table :deep(.ant-table) {
  font-size: 12px;
}

.ltv-table :deep(.ant-table-thead > tr > th) {
  padding: 4px 8px;
  font-size: 12px;
}

.ltv-table :deep(.ant-table-tbody > tr > td) {
  padding: 4px 8px;
  font-size: 12px;
}

/* 汇总行样式 */
.ltv-table :deep(.ant-table-summary) {
  background: #fafafa;
}

.ltv-table :deep(.ant-table-summary td) {
  padding: 4px 8px;
  font-size: 12px;
}

/* 预估回本表格 */
.estimate-table {
  border-collapse: collapse;
  font-size: 12px;
  min-width: 100%;
}

.estimate-table th,
.estimate-table td {
  padding: 4px 10px;
  white-space: nowrap;
  border: 1px solid #e8e8e8;
}

.estimate-table th {
  background: #fafafa;
  font-weight: 600;
  text-align: center;
}

/* 折叠标题 */
.ltv-collapse-toggle {
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
}

.ltv-collapse-toggle:hover {
  color: #1677ff;
}

/* 留存分析卡片 */
.retention-card :deep(.ant-card-head-title) {
  display: flex;
  align-items: center;
}

.retention-center-title {
  flex: 1;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.retention-table-wrap {
  overflow-x: auto;
  overflow-y: auto;
}

.retention-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  white-space: nowrap;
}

.rt-th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8f9fb;
  padding: 8px 14px;
  text-align: center;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eceef2;
}

.rt-th.rt-fixed {
  position: sticky;
  left: 0;
  z-index: 3;
  text-align: center;
}

.rt-td {
  padding: 4px 14px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}

.rt-rate {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
}

.rt-sub {
  font-size: 11px;
  opacity: 0.55;
  margin-top: 2px;
  font-weight: 400;
}

.rt-td.rt-fixed {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  font-weight: 500;
  text-align: center;
  color: #333;
}

.retention-table tbody tr:hover td {
  background: rgba(24, 144, 255, 0.04);
}

.retention-table tbody tr:hover td.rt-fixed {
  background: #f0f7ff;
}
</style>

<style>
/* 留存分析指标说明弹层（非 scoped） */
.retention-help-popover .ant-popover-inner {
  max-width: 420px;
}

.rh-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 13px;
  line-height: 1.7;
  color: #444;
}

.rh-label {
  font-weight: 600;
  color: #222;
  margin-right: 4px;
}
</style>
